import React, { useState, useRef, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { UPDATE_SKILL } from '../../Reducers/Actions';
import { Grid, Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import FormInput from '../../SystemDesign/FormInput';
import CardStepper from '../../SystemDesign/CardStepper';
import FormSelect from '../../SystemDesign/FormSelect';
import { useUpdateSkillMutation, useAddSkillToUnitsMutation } from '../../services/SkillsManagement';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import MuiDialog from '../../SystemDesign/MuiDialog';
import EditIcon from '@mui/icons-material/Edit';
import ColoredSelector from '../../SystemDesign/ColoredSelector';

const UpdateSkill = ({ open, onClose, skill, units = [], onSubmit }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Use SnackBar context for displaying messages
  const { setOpenSnackBar, setInfoAlert } = useContext(SnackBarContext);

  // Create a ref to access Formik methods from outside the Formik component
  const formRef = useRef(null);

  // Form state
  const [formError, setFormError] = useState('');

  // RTK Query mutation hook
  const [updateSkillMutation, { isLoading }] = useUpdateSkillMutation();
  const [addSkillToUnits, { isLoading: isAddingToUnits }] = useAddSkillToUnitsMutation();

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});

  // Step labels
  const steps = ['Basic Information', 'Skill Levels', 'References & Comments'];

  // Prepare initial form values
  const getInitialValues = () => {
    // Guard: avoid noisy logs and null access when skill is not provided
    if (!skill) return {
      code: '',
      name: '',
      description: '',
      priority: 'Medium',
      unit_id: [],

      awareness_description: '',
      application_description: '',
      management_description: '',
      external_reference: '',
      internal_reference: '',
      comments: ''
    };

    return {
      code: skill?.skill_code || '',
      name: skill?.name || '',
      description: skill?.description || '',
      priority: skill?.priority ?
        skill.priority.charAt(0).toUpperCase() + skill.priority.slice(1) : 'Medium',
      // Preselect current unit as the first selection in multi-select
      unit_id: skill?.unit_id ? [Number(skill.unit_id)] : [],

      awareness_description: skill?.awareness_description || '',
      application_description: skill?.skilled_application_description || '',
      management_description: skill?.management_description || '',
      external_reference: skill?.external_reference || '',
      internal_reference: skill?.internal_reference || '',
      comments: skill?.comments || ''
    };
  };

  // Set form values state
  const [formValues, setFormValues] = useState(getInitialValues());

  // Priority options
  const priorityOptions = [
    { id: 'Low', name: 'Low', title: 'Low', color: colors.green[400] },
    { id: 'Medium', name: 'Medium', title: 'Medium', color: colors.Orange[400] },
    { id: 'High', name: 'High', title: 'High', color: colors.redAccent[400] }
  ];

  // Update form values when skill changes
  useEffect(() => {
    if (skill) {
      setFormValues(getInitialValues());
      // Reset stepper
      setActiveStep(0);
      setCompleted({});
    }
  }, [skill]);

  // Validation schema
  const validationSchema = Yup.object({

    // Step 1
    code: Yup.string().required('Skill code is required'),
    name: Yup.string().required('Skill name is required'),
    description: Yup.string().required('Description is required'),
    priority: Yup.string().required('Priority is required'),
    unit_id: Yup.array().of(Yup.number().typeError('Unit must be a number')).min(1, 'Select at least one unit').required('Select at least one unit'),

    // Step 2
    awareness_description: Yup.string(),
    application_description: Yup.string(),
    management_description: Yup.string(),

    // Step 3
    external_reference: Yup.string(),
    internal_reference: Yup.string(),
    comments: Yup.string()
  });

  // Form fields configuration by step
  const step1Fields = [
    { name: 'code', label: 'Skill Code', type: 'text', placeholder: 'Ex: PBED, H0101...', required: true },
    { name: 'name', label: 'Skill Name', type: 'text', placeholder: 'Ex: Work Preparation & Debrief...', required: true },
    {
      type: 'select',
      name: 'priority',
      label: 'Priority',
      required: true,
      options: priorityOptions,
      disabled: false
    },
    {
      type: 'select',
      name: 'unit_id',
      label: 'Units',
      required: true,
      multiple: true,
      options: units.map(unit => ({ 
        id: Number(unit.unit_id || unit.id), 
        title: unit.specific_unit || unit.title
      })),
    },
    { name: 'description', label: 'Description', type: 'text', placeholder: 'Describe the skill...', required: true, multiline: true, rows: 3 }
  ];

  const step2Fields = [
    { name: 'awareness_description', label: 'Awareness Description', type: 'text', placeholder: 'Describe awareness level...', required: false, multiline: true, rows: 3 },
    { name: 'application_description', label: 'Skill Application Description', type: 'text', placeholder: 'Describe application level...', required: false, multiline: true, rows: 3 },
    { name: 'management_description', label: 'Management Description', type: 'text', placeholder: 'Describe management level...', required: false, multiline: true, rows: 3 }
  ];

  const step3Fields = [
    { name: 'external_reference', label: 'External Reference', type: 'text', placeholder: 'External documentation or resources...', required: false, multiline: true, rows: 2 },
    { name: 'internal_reference', label: 'Internal Reference', type: 'text', placeholder: 'Internal documentation or resources...', required: false, multiline: true, rows: 2 },
    { name: 'comments', label: 'Comments', type: 'text', placeholder: 'Any additional notes...', required: false, multiline: true, rows: 2 }
  ];

  // Get grid sizing for form fields
  function getFieldGridProps(field) {
    switch (field.name) {
      case 'code':
      case 'name':
        return { xs: 6, md: 6 };
      case 'priority':
      case 'unit_id':
        return { xs: 12, md: 12 };
      case 'description':
      case 'comments':
        return { xs: 12, md: 12 };
      case 'awareness_description':
      case 'application_description':
      case 'management_description':
        return { xs: 12, md: 12 };
      case 'external_reference':
      case 'internal_reference':
        return { xs: 12, md: 12 };
      default:
        return { xs: 12, md: 6 };
    }
  }

  // Validate step before navigation
  const validateStep = (formik, step, onSuccess) => {
    // Touch all fields in current step to trigger validation
    touchAllFieldsInStep(formik, step);

    // Validate after touching
    formik.validateForm().then(errors => {
      if (isStepValid(formik.values, errors, step)) {
        // If valid, execute the success callback
        onSuccess();
      } else if (step === 0) {
        // Only show error message for step 1 (which has required fields)
        setInfoAlert({
          severity: 'error',
          message: 'Please fill out all required fields before proceeding'
        });
        setOpenSnackBar(true);
      }
    });
  };

  // Handle step completion
  const handleComplete = (formik) => {
    const newCompleted = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);

    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  // Custom step handlers to enforce validation
  const handleNext = () => {
    const formik = formRef.current;
    if (formik && activeStep === 0) {
      validateStep(formik, activeStep, () => {
        setActiveStep(activeStep + 1);
      });
    } else {
      // For other steps, no validation needed
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleStepClick = (step) => {
    const formik = formRef.current;
    if (formik && activeStep === 0 && step > activeStep) {
      // If trying to move forward from step 1, validate
      validateStep(formik, activeStep, () => {
        setActiveStep(step);
      });
    } else {
      // For backward navigation or from other steps, allow freely
      setActiveStep(step);
    }
  };

  // Check if current step is valid
  const isStepValid = (values, errors, step) => {
    let stepFields;
    switch(step) {
      case 0:
        stepFields = ['code', 'name', 'description', 'priority', 'unit_id'];
        break;
      case 1:
        return true;
      case 2:
        return true;
      default:
        return false;
    }

    // Check if all required fields in this step are valid
    return stepFields.every(field => {
      return !errors[field];
    });
  };

  // Touch all fields in the current step to trigger validation
  const touchAllFieldsInStep = (formik, step) => {
    let stepFields;
    switch(step) {
      case 0:
        stepFields = ['code', 'name', 'description', 'priority', 'unit_id'];
        break;
      case 1:
        stepFields = ['awareness_description', 'application_description', 'management_description'];
        break;
      case 2:
        stepFields = ['external_reference', 'internal_reference', 'comments'];
        break;
      default:
        stepFields = [];
    }

    const touchedFields = {};
    stepFields.forEach(field => {
      touchedFields[field] = true;
    });

    formik.setTouched({ ...formik.touched, ...touchedFields }, true);
  };

  // Handle form submission
  const handleSubmit = async (values, actions) => {
    if (!skill) return;

    try {
      setFormError('');

      // From multi-select units: use the first as base, others for duplication
      const selectedUnits = Array.isArray(values.unit_id) ? values.unit_id : [];
      const baseUnitId = Number.isFinite(parseInt(selectedUnits[0], 10)) ? parseInt(selectedUnits[0], 10) : 0;

      // Ensure all string fields are sent as empty strings instead of null/undefined
      const ensureString = (value) => {
        if (value === undefined || value === null) return '';
        return typeof value === 'string' ? value.trim() : String(value);
      };

      // Prepare the request body
      const requestBody = {
        skill_code: ensureString(values.code),
        skill_name: ensureString(values.name),
        name: ensureString(values.name),
        description: ensureString(values.description),
        priority: values.priority.toLowerCase(),
        unit_id: baseUnitId,

        awareness_description: ensureString(values.awareness_description),
        skilled_application_description: ensureString(values.application_description),
        management_description: ensureString(values.management_description),
        external_reference: ensureString(values.external_reference),
        internal_reference: ensureString(values.internal_reference),
        comments: ensureString(values.comments)
      };

      // Call the API to update the skill
      const result = await updateSkillMutation({
        skillId: skill.skill_id,
        body: requestBody
      }).unwrap();

      // Find the complete unit object from our units data
      const selectedUnit = units.find(u => u.unit_id === baseUnitId || u.id === baseUnitId);

      // Create a fully hydrated unit object
      const fullUnitObject = selectedUnit ? {
        ...selectedUnit,
        unit_id: selectedUnit.unit_id || selectedUnit.id,
        id: selectedUnit.id || selectedUnit.unit_id,
        // Include discipline data if available
        discipline: selectedUnit.discipline || null
      } : { unit_id: baseUnitId };

      // Prepare the skill for Redux store with consistent field names
      const updatedSkillForStore = {
        ...skill,
        skill_id: skill.skill_id,
        id: skill.id,
        skill_code: values.code,
        skill_name: values.name,
        name: values.name,
        description: values.description,
        priority: values.priority.toLowerCase(),
        unit_id: baseUnitId,
        awareness_description: values.awareness_description,
        skilled_application_description: values.application_description,
        management_description: values.management_description,
        external_reference: values.external_reference,
        internal_reference: values.internal_reference,
        comments: values.comments,
        updatedAt: new Date().toISOString(),
        unit: skill.unit || fullUnitObject
      };

      // Update Redux store
      dispatch({ type: UPDATE_SKILL, payload: updatedSkillForStore });

      // If multiple units selected, duplicate to the remaining ones
      let duplicateSummary = '';
      const unitIds = [...new Set((selectedUnits.slice(1) || []).map((v) => parseInt(v, 10)).filter((id) => Number.isFinite(id) && id !== baseUnitId))];
      if (unitIds.length > 0) {
        try {
          const dupResp = await addSkillToUnits({ body: { skillId: skill.skill_id, unitIds } }).unwrap();
          const data = dupResp?.data || dupResp || {};
          const created = data.createdCount ?? 0;
          const skippedExisting = data.skippedExisting ?? 0;
          const skippedSameUnit = data.skippedSameUnit ?? 0;
          const failed = data.failedCount ?? 0;
          duplicateSummary = ` | Duplicated: ${created} created, ${skippedExisting} existed, ${skippedSameUnit} same unit, ${failed} failed`;
        } catch (dupErr) {
          console.error('Error duplicating skill to units (update):', dupErr);
          duplicateSummary = ' | Duplication failed';
        }
      }

      // Show success message
      setInfoAlert({
        severity: 'success',
        message: `Skill "${values.code} - ${values.name}" successfully updated${duplicateSummary}`
      });

      setOpenSnackBar(true);

      // If the onSubmit prop exists, call it with the updated skill
      if (onSubmit) {
        onSubmit(updatedSkillForStore);
      }

      
      // Reset form and close dialog
      actions.resetForm();
      onClose();
    } catch (error) {
      console.error('Failed to update skill:', error);
      const errorMessage = error?.data?.message || 'Failed to update skill';
      
      // Set form error
      setFormError(errorMessage);
      
      // Also show in snackbar for better visibility
      setInfoAlert({
        severity: 'error',
        message: errorMessage
      });
      setOpenSnackBar(true);
      
      actions.setSubmitting(false);
    }
  };
  
  // Render a field based on its configuration
  const renderField = (field, formikProps) => {
    const gridProps = getFieldGridProps(field);
    
    if (field.type === 'select') {
      // For Units we need a multiple select (FormSelect); others (like Priority) keep ColoredSelector
      if (field.name === 'unit_id') {
        return (
          <Grid item size={{...gridProps}} key={field.name}>
            <FormSelect
              name={field.name}
              label={field.label}
              multiple
              options={field.options}
              value={formikProps.values[field.name]}
              onChange={(e) => {
                const selected = e?.target?.value || [];
                formikProps.setFieldValue(field.name, Array.isArray(selected) ? selected : [selected]);
              }}
              error={formikProps.touched[field.name] && Boolean(formikProps.errors[field.name])}
              helperText={formikProps.touched[field.name] && formikProps.errors[field.name]}
              disabled={field.disabled}
            />
          </Grid>
        );
      }

      // Default select rendering using ColoredSelector
      return (
        <Grid item size={{...gridProps}} key={field.name}>
          <ColoredSelector
            options={field.options}
            value={formikProps.values[field.name]}
            onChange={(value) => formikProps.setFieldValue(field.name, value)}
            placeholder={`Select ${field.label.toLowerCase()}`}
            label={field.label}
            valueKey="id"
            labelKey="title"
            colorKey="color"
            searchable={field.name !== 'priority'}
            required={field.required}
            error={formikProps.touched[field.name] && Boolean(formikProps.errors[field.name])}
            helperText={formikProps.touched[field.name] && formikProps.errors[field.name]}
            disabled={field.disabled}
            showColorDot={true}
          />
        </Grid>
      );
    }
    
    return (
      <Grid item size={{...gridProps}} key={field.name}>
        <FormInput
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          multiline={field.multiline}
          rows={field.rows}
          required={field.required}
        />
      </Grid>
    );
  };

  // Create dialog content for MuiDialog
  const dialogContent = (isOpen) => {
    // Lazy guard to avoid rendering heavy tree when dialog is closed or skill missing
    if (!isOpen || !skill) return <></>;

    return (
    <Formik
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
      validateOnMount={true}
      enableReinitialize={true}
    >
      {(formikProps) => {
        return (
          <Form>
            <CardStepper
              steps={steps}
              activeStep={activeStep}
              setActiveStep={(step) => handleStepClick(step)}
              completed={completed}
              setCompleted={setCompleted}
              handleNext={handleNext}
              handleBack={handleBack}
              handleComplete={() => handleComplete(formikProps)}
            >
              {/* Step 1: Basic Information */}
              {activeStep === 0 && (
                <Grid container spacing={2}>
                  {step1Fields.map(field => renderField(field, formikProps))}
                </Grid>
              )}
              
              {/* Step 2: Skill Levels */}
              {activeStep === 1 && (
                <Grid container spacing={2}>
                  {step2Fields.map(field => renderField(field, formikProps))}
                </Grid>
              )}
              
              {/* Step 3: References & Comments */}
              {activeStep === 2 && (
                <Grid container spacing={2}>
                  {step3Fields.map(field => renderField(field, formikProps))}
                </Grid>
              )}
            </CardStepper>
            
            {formError && (
              <Box sx={{ color: 'error.main', mb: 2, mt: 2 }}>
                {formError}
              </Box>
            )}
          </Form>
        );
      }}
    </Formik>
    );
  };
  
  return (
    <MuiDialog
    maxWidth="md"
      open={open}
      onClose={onClose}
      icon={<EditIcon />}
      title="Edit Skill"
      subtitle="Update the skill details"
      content={dialogContent}
      existAction={true}
      actionTitle="Update"
      actionType="submit"
    
      actionFunction={() => {
        if (formRef.current) {
          formRef.current.handleSubmit();
        }
      }}
      isLoading={isLoading}
      disableAction={!(formRef.current?.isValid && formRef.current?.dirty)}
    />
  );
};

export default UpdateSkill;